import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../contexts/ProvinceContext';
import { useKingdomRelationships } from '../hooks/useKingdomRelationships';
import { useUnreadCount } from '../hooks/useUnreadCount';
import { NAV_PILLAR_COLORS } from '../constants/navPillars';
import { NAV_SECTIONS, type NavItemData } from '../constants/sidebarNav';

// ─── Nav data types and structure ─────────────────────────────────────────────

// ─── NavIcon ──────────────────────────────────────────────────────────────────

function NavIcon({ d, color }: { d: string; color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d={d} stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── NavItem ──────────────────────────────────────────────────────────────────

interface NavItemProps {
  item: NavItemData;
  currentPath: string;
  onClick: () => void;
  disabled?: boolean;
  badge?: number;
}

function NavItem({ item, currentPath, onClick, disabled = false, badge }: NavItemProps) {
  // SF-150: "My Kingdom" now points at /kingdom/roster (the hub screen owns bare /kingdom), so
  // no Sidebar item has route === '/kingdom' anymore and the SF-134 exact-match special case is
  // dead — the generic default rule below is correct and sufficient on its own.
  const isActive = currentPath === item.route || currentPath.startsWith(item.route + '/');

  const pillar = NAV_PILLAR_COLORS[item.pillar];

  if (disabled) {
    return (
      <span
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px',
          borderLeft: '2px solid transparent',
          color: '#353f62',
          cursor: 'not-allowed',
          fontSize: 13,
        }}
      >
        <NavIcon d={item.iconPath} color="#353f62" />
        {item.label}
      </span>
    );
  }

  return (
    <Link
      to={item.route}
      aria-current={isActive ? 'page' : undefined}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px',
        borderLeft: isActive ? `2px solid ${pillar.primary}` : '2px solid transparent',
        ...(isActive ? { backgroundColor: pillar.bgTint } : {}),
        color: isActive ? '#d7dae8' : '#7b83a5',
        fontWeight: isActive ? 600 : 400,
        textDecoration: 'none',
      }}
      className={
        isActive
          ? 'focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#6a9e8e]'
          : 'hover:bg-game-750 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#6a9e8e]'
      }
    >
      <NavIcon d={item.iconPath} color={isActive ? pillar.primary : '#7b83a5'} />
      <span style={{ fontSize: 13, flex: 1 }}>{item.label}</span>
      {badge != null && badge > 0 && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            backgroundColor: '#c45a4a',
            color: '#fff',
            borderRadius: 8,
            padding: '1px 5px',
            minWidth: 16,
            textAlign: 'center',
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar() {
  const { logout } = useAuth();
  const { province, refetch } = useProvince();
  const { count: unreadCount } = useUnreadCount();
  const location = useLocation();
  const currentPath = location.pathname;

  const ownKingdomId = province?.metadata?.location
    ? `${province.metadata.location.island}:${province.metadata.location.kingdom}`
    : null;
  const { data: ownRelationships } = useKingdomRelationships(ownKingdomId);
  const warRelationships = ownRelationships?.relationships.filter((r) => r.relationship === 'WAR') ?? [];

  const handleNavClick = () => {
    refetch();
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    // Sidebar — desktop (lg+) only: sticky in the flex layout, sticks at viewport top as page scrolls
    <aside
      className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen lg:self-start flex-shrink-0 flex-col overflow-y-auto"
      style={{
        width: 160,
        borderRight: '1px solid #22293f',
        backgroundColor: '#0b0e19',
      }}
    >
      {/* Desktop: Galdorn branding zone — matches ResourceBar height */}
      <div
        className="hidden lg:flex items-center"
        style={{
          height: 58,
          padding: '0 16px',
          borderBottom: '1px solid #22293f',
        }}
      >
        <span style={{ color: '#d7dae8', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em' }}>
          Galdorn
        </span>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1 }}>
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} style={{ marginBottom: 4 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#7b83a5',  // sidebar-ink-dim (WCAG AA compliant, per Design Guide)
                padding: '10px 16px 4px',
              }}
            >
              {section.title}
            </div>
            {section.items.map((item) => (
              <NavItem
                key={item.route}
                item={item}
                currentPath={currentPath}
                onClick={handleNavClick}
                disabled={item.disabled}
                badge={item.route === '/recent-news' ? unreadCount : undefined}
              />
            ))}
            {/* War quick-jump chips — only under Kingdom section (content-based grouping: "your
                kingdom is at war" is a Kingdom-section fact regardless of the link's destination).
                SF-134: link target moved to /world/dossier; real <Link>s per the project's house
                navigation-link pattern. The manual refetch() bundled into the old onClick handler
                is dropped as redundant, not because Link makes it unnecessary — ProvinceContext's
                own pathname-keyed effect already re-fetches province data on every navigation
                (including NavItem's handleNavClick refetch() above, which is equally redundant
                but left alone as a pre-existing pattern, out of scope here). */}
            {section.title === 'Kingdom' && warRelationships.length > 0 && (
              <div style={{ padding: '2px 16px 6px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {warRelationships.slice(0, 3).map((r) => (
                  <Link
                    key={r.kingdomId}
                    to={`/world/dossier/${r.kingdomId}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '3px 0 3px 8px',
                      fontSize: 11,
                      color: '#c45a4a',
                      borderLeft: '2px solid #c45a4a',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    ⚔ {r.kingdomName ?? r.kingdomId}
                  </Link>
                ))}
                {warRelationships.length > 3 && (
                  <Link
                    to="/world/dossier"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '3px 0 3px 8px',
                      fontSize: 11,
                      color: '#c45a4a',
                      borderLeft: '2px solid #c45a4a',
                      textDecoration: 'none',
                    }}
                  >
                    ⚔ Wars ({warRelationships.length})
                  </Link>
                )}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Profile + Logout footer */}
      <div style={{ borderTop: '1px solid #22293f', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Link
          to="/profile"
          className="hover:text-white transition-colors"
          style={{ color: '#7b83a5', fontSize: 13, textDecoration: 'none' }}
        >
          Profile
        </Link>
        <button
          onClick={handleLogout}
          className="w-full text-left hover:text-white transition-colors"
          style={{ color: '#7b83a5', fontSize: 13 }}
        >
          Logout
        </button>
      </div>
    </aside>
  );
}
